export type ProductType = 'favour' | 'asoebi' | 'decor' | 'accessories' | 'internal' | 'fabric';

export type StockMovementReason = 'received' | 'sold' | 'returned' | 'damaged' | 'adjustment' | 'initial';

export const STOCK_MOVEMENT_REASON_LABELS: Record<StockMovementReason, string> = {
  received: 'Received from supplier',
  sold: 'Sold / Distributed',
  returned: 'Returned',
  damaged: 'Damaged / Write-off',
  adjustment: 'Manual adjustment',
  initial: 'Initial stock',
};

export interface StockMovement {
  id: string;
  type: ProductType;
  quantity: number;
  reason: StockMovementReason;
  note: string;
  created_at: string;
}

export interface ProductSupplier {
  id: string;
  name: string;
  costNGN?: number | null;
  costUSD?: number | null;
  costCNY?: number | null;
  minQuantity?: number;
  leadTime?: string;
  link?: string;
  isPreferred?: boolean;
  notes?: string;
}

export interface Product {
  id: string;
  name: string;
  types: ProductType[];
  suppliers: ProductSupplier[];
  stockPools: Partial<Record<ProductType, number>>; // qty owned per type
  stock_movements?: StockMovement[];
  image?: string;
  description?: string;
  customizable?: boolean;
  archived?: boolean;
  created_at: string;
  updated_at: string;
}

export interface UnifiedBundleItem {
  productId: string;       // references Product.id (or FavorItem.id or Fabric.id during transition)
  productName: string;     // denormalized for display
  quantity: number;
  unitLabel?: string;      // 'yds', 'pcs', 'sets', 'boxes' etc.
}

export interface UnifiedBundle {
  id: string;
  name: string;
  types: ProductType[];          // which sections show this bundle
  items: UnifiedBundleItem[];
  priceMode: 'manual' | 'auto'; // auto = sum of item costs, manual = override
  price: number;                 // manual price (or 0 if auto)
  image?: string;
  description?: string;
  eventIds?: string[];
  is_active: boolean;
  recipientIds?: string[];       // for direct assignment to guests
  /** Bundle-specific payment reference (e.g. 'EKOCE-F') and free-text
   *  instructions (deadlines, which account to pay into). These override the
   *  single project-wide paymentMethod, which forced every bundle to share
   *  one reference code. */
  paymentReference?: string;
  paymentInstructions?: string;
  /** Per-unit cost target for this bundle, in `targetCurrency`. Owned by the
   *  `bundles.target_cost` / `target_currency` columns and read by the
   *  over-target insight rule. Editable from the bundle form; pass `null`
   *  to clear an existing target. */
  targetCost?: number | null;
  targetCurrency?: string | null;
  archived?: boolean;
  /** Set once the bundle has been moved to trash (stage 2 of the
   *  archive → trash → permanent-delete lifecycle). Only a bundle already
   *  in trash can be permanently deleted. */
  deletedAt?: string | null;
  created_at: string;
  updated_at: string;
}
